import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { formatMessageContent, isMultiSelectMessage, stripSelectionTags, extractOptions, stripOptionLines } from '../../utils/messageFormatter';
import { Keyboard } from '@capacitor/keyboard';

const TEXTAREA_MAX_HEIGHT = 120;
const TEXTAREA_MIN_HEIGHT = 48;
const TEXTAREA_FONT_SIZE = 16;
const RESIZE_OBSERVER_DEBOUNCE_MS = 300;
const KEYBOARD_HEIGHT_THRESHOLD = 150;
const FOCUS_OUT_DELAY_MS = 100;
const MOBILE_BREAKPOINT_PX = 768;
const SCROLL_DELAY_MS = 50;
const KEYBOARD_SCROLL_DELAY_MS = 260;
const SCROLL_IMMEDIATE_DELAY_MS = 10;
const SCROLL_SHORT_DELAY_MS = 50;
const SCROLL_MEDIUM_DELAY_MS = 100;
const MESSAGES_CONTAINER_Z_INDEX = 40;
const INPUT_CONTAINER_Z_INDEX = 50;

export interface Message {
  role: 'coach' | 'user';
  content: string;
}

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  showContinueButton?: boolean;
  onContinue?: () => void;
  hasBottomNav?: boolean;
  isContinueDisabled?: boolean;
  isLimitReached?: boolean;
  limitMessage?: string;
  autoContinueMode?: boolean;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  onSendMessage,
  placeholder = "Type your message...",
  isLoading = false,
  showContinueButton = false,
  onContinue,
  hasBottomNav = true,
  isContinueDisabled = false,
  isLimitReached = false,
  limitMessage = "You've reached your weekly new session limit. You will be able to create new sessions again on Sunday.",
  autoContinueMode = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardInsetHeight, setKeyboardInsetHeight] = useState(0);
  const [isAndroidMobileWeb, setIsAndroidMobileWeb] = useState(false);
  const [multiSelectChoices, setMultiSelectChoices] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const initialViewportHeight = useRef<number>(0);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = TEXTAREA_MAX_HEIGHT; // Maximum height in pixels
      const newHeight = Math.min(scrollHeight, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  // Debug: Log isLimitReached value
  useEffect(() => {
    console.log('[ChatInterface] isLimitReached value:', isLimitReached, 'Type:', typeof isLimitReached);
  }, [isLimitReached]);

  // Initialize viewport height and set up keyboard accessory bar
  useEffect(() => {
    initialViewportHeight.current = window.innerHeight;
    
    // Detect Android mobile web platform
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isAndroid = /android/i.test(userAgent);
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
    const isCapacitor = (window as any).Capacitor !== undefined;
    const platform = isCapacitor ? (window as any).Capacitor?.getPlatform() : 'web';
    const isNativePlatform = platform === 'ios' || platform === 'android';
    
    // Only apply CSS solution for Android mobile web (not Capacitor/native)
    setIsAndroidMobileWeb(isAndroid && isMobile && !isCapacitor);
    
    // Enable keyboard accessory bar (Done button) in Capacitor native platforms only
    if (isCapacitor && isNativePlatform) {
      try {
        Keyboard.setAccessoryBarVisible({ isVisible: true });
      } catch (error) {
        console.warn('[ChatInterface] Keyboard plugin not available:', error);
      }
    }
    
    return () => {
      if (isCapacitor && isNativePlatform) {
        try {
          Keyboard.setAccessoryBarVisible({ isVisible: false });
        } catch (error) {
          // Silently fail on cleanup
        }
      }
    };
  }, []);

  // Use ResizeObserver to detect container size changes - but debounce to avoid jumping
  useEffect(() => {
    if (!messagesContainerRef.current) return;
    
    let resizeTimeout: NodeJS.Timeout;
    
    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize events to avoid scrolling during transitions
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (messagesContainerRef.current) {
          // Only scroll after resize is complete and transition is done
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, RESIZE_OBSERVER_DEBOUNCE_MS);
    });
    
    resizeObserver.observe(messagesContainerRef.current);
    
    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, []);

  // Enhanced keyboard detection for Capacitor - using native events for instant response
  useEffect(() => {
    const isCapacitor = (window as any).Capacitor !== undefined;
    const platform = isCapacitor ? (window as any).Capacitor?.getPlatform() : 'web';
    const isNativePlatform = platform === 'ios' || platform === 'android';
    
    // Use native Keyboard plugin events for instant response (fires before animation)
    if (isCapacitor && isNativePlatform) {
      let keyboardWillShowListener: any;
      let keyboardWillHideListener: any;

      // Setup listeners asynchronously with error handling
      (async () => {
        try {
          keyboardWillShowListener = await Keyboard.addListener('keyboardWillShow', () => {
            // Fire immediately on keyboard show event (no delay)
            setIsKeyboardOpen(true);
          });

          keyboardWillHideListener = await Keyboard.addListener('keyboardWillHide', () => {
            // Fire immediately on keyboard hide event (no delay)
            setIsKeyboardOpen(false);
          });
        } catch (error) {
          console.warn('[ChatInterface] Keyboard plugin not available, falling back to web detection:', error);
          // Fall through to web detection below
        }
      })();

      // Fallback to resize for height calculation (no longer needed but kept for future use)
      const handleResize = () => {
        // Height calculation could be used here if needed in future
      };

      // Also use focus events for instant response (faster than resize)
      const handleFocusIn = () => {
        setIsKeyboardOpen(true);
      };

      const handleFocusOut = () => {
        setTimeout(() => {
          const currentHeight = window.innerHeight;
          const heightDiff = initialViewportHeight.current - currentHeight;
          if (heightDiff <= KEYBOARD_HEIGHT_THRESHOLD) {
            setIsKeyboardOpen(false);
          }
        }, FOCUS_OUT_DELAY_MS);
      };

      window.addEventListener('resize', handleResize);
      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        try {
          if (keyboardWillShowListener) {
            keyboardWillShowListener.remove();
          }
          if (keyboardWillHideListener) {
            keyboardWillHideListener.remove();
          }
        } catch (error) {
          // Silently fail on cleanup if listeners weren't properly initialized
        }
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    } else {
      // Web browser fallback - ONLY on mobile web (< 768px), not desktop
      const checkMobile = () => window.innerWidth < MOBILE_BREAKPOINT_PX;
      
      if (!checkMobile()) {
        // Desktop web: don't track keyboard state
        return;
      }

      const handleResize = () => {
        // Only track on mobile
        if (!checkMobile()) {
          setIsKeyboardOpen(false);
          if (isAndroidMobileWeb) {
            setKeyboardInsetHeight(0);
          }
          return;
        }
        
        // For Android mobile web, use Visual Viewport API for accurate keyboard height
        if (isAndroidMobileWeb && window.visualViewport) {
          const visualHeight = window.visualViewport.height;
          const layoutHeight = window.innerHeight;
          const heightDiff = initialViewportHeight.current - visualHeight;
          
          if (heightDiff > KEYBOARD_HEIGHT_THRESHOLD) {
            // Calculate keyboard inset height: difference between layout and visual viewport
            // This is the space taken by the keyboard
            // The keyboard top is at this distance from layout viewport bottom
            const calculatedInset = layoutHeight - visualHeight;
            setIsKeyboardOpen(true);
            setKeyboardInsetHeight(calculatedInset);
            // Debug logging (remove in production)
            console.log('Android keyboard calculation:', {
              layoutHeight,
              visualHeight,
              calculatedInset,
              heightDiff
            });
          } else {
            setIsKeyboardOpen(false);
            setKeyboardInsetHeight(0);
          }
        } else {
          // For other platforms, use simple height difference
          const currentHeight = window.innerHeight;
          const heightDiff = initialViewportHeight.current - currentHeight;
          
          if (heightDiff > KEYBOARD_HEIGHT_THRESHOLD) {
            setIsKeyboardOpen(true);
          } else {
            setIsKeyboardOpen(false);
          }
        }
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
        return () => {
          window.visualViewport?.removeEventListener('resize', handleResize);
        };
      } else {
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
        };
      }
    }
  }, [isAndroidMobileWeb]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  // Clear multi-select choices when messages change (new response received)
  useEffect(() => {
    setMultiSelectChoices(new Set());
  }, [messages.length]);

  useEffect(() => {
    // Use immediate scroll for better control
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    };
    
    scrollToBottom();
    // Try again after a brief delay to catch layout updates
    setTimeout(scrollToBottom, SCROLL_DELAY_MS);
  }, [messages, isLoading]);

  // Set CSS custom property for keyboard inset height (Android mobile web only)
  // This acts as a fallback for env(keyboard-inset-height) which has limited support
  useEffect(() => {
    if (isAndroidMobileWeb) {
      document.documentElement.style.setProperty('--keyboard-inset-height', `${keyboardInsetHeight}px`);
      return () => {
        document.documentElement.style.removeProperty('--keyboard-inset-height');
      };
    }
  }, [keyboardInsetHeight, isAndroidMobileWeb]);

  // Recalculate keyboard inset height when keyboard opens (Android mobile web only)
  useEffect(() => {
    if (isAndroidMobileWeb && isKeyboardOpen && window.visualViewport) {
      const recalculate = () => {
        const visualHeight = window.visualViewport!.height;
        const layoutHeight = window.innerHeight;
        const calculatedInset = layoutHeight - visualHeight;
        if (calculatedInset > 0) {
          setKeyboardInsetHeight(calculatedInset);
        }
      };
      
      // Recalculate immediately and after keyboard animation
      recalculate();
      const timeout1 = setTimeout(recalculate, 100);
      const timeout2 = setTimeout(recalculate, 250);
      const timeout3 = setTimeout(recalculate, 400);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    } else if (isAndroidMobileWeb && !isKeyboardOpen) {
      setKeyboardInsetHeight(0);
    }
  }, [isKeyboardOpen, isAndroidMobileWeb]);

  // Scroll to bottom when keyboard opens - wait for transition to complete
  useEffect(() => {
    if (isKeyboardOpen) {
      // Wait for the transition to complete (250ms) before scrolling to avoid visual jumps
      const scrollToBottom = () => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      };
      
      // Scroll after transition completes
      const timeout = setTimeout(scrollToBottom, KEYBOARD_SCROLL_DELAY_MS);
      
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isKeyboardOpen]);

  const handleFocus = () => {
    // Don't allow focus if limit is reached
    if (isLimitReached) {
      console.log('[ChatInterface] Blocked focus - isLimitReached:', isLimitReached);
      if (textareaRef.current) {
        textareaRef.current.blur();
      }
      return;
    }
    
    // Prevent zoom by ensuring font size is at least 16px
    if (textareaRef.current) {
      textareaRef.current.style.fontSize = `${TEXTAREA_FONT_SIZE}px`;
    }
    // Immediately set keyboard open for instant UI response
    setIsKeyboardOpen(true);
    
    // Calculate keyboard inset height immediately for Android mobile web
    if (isAndroidMobileWeb && window.visualViewport) {
      const visualHeight = window.visualViewport.height;
      const layoutHeight = window.innerHeight;
      const calculatedInset = layoutHeight - visualHeight;
      if (calculatedInset > 0) {
        setKeyboardInsetHeight(calculatedInset);
      }
    }
    
    // Force immediate scroll to bottom when input is focused
    const scrollNow = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'nearest' });
      }
    };
    
    scrollNow();
    setTimeout(scrollNow, SCROLL_IMMEDIATE_DELAY_MS);
    setTimeout(scrollNow, SCROLL_SHORT_DELAY_MS);
    setTimeout(scrollNow, SCROLL_MEDIUM_DELAY_MS);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLimitReached) return;
    onSendMessage(inputValue.trim());
    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (isLimitReached) {
      console.log('[ChatInterface] Blocked keyPress - isLimitReached:', isLimitReached);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const Typing = () => {
    return (
      <div className="typing bg-transparent p-1 rounded-lg">
        <div className="typing-dot"></div>
        <div className="typing-dot"></div>
        <div className="typing-dot"></div>
      </div>
    )
  }

  // Detect if we're on desktop (>= 768px) - use normal flow instead of fixed positioning
  const [isDesktop, setIsDesktop] = useState(false);
  
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= MOBILE_BREAKPOINT_PX);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // When keyboard is open, bottom nav is hidden, so don't account for its space
  const effectiveHasBottomNav = hasBottomNav && !isKeyboardOpen;
  
  // Calculate positions - only for mobile (desktop uses normal flow)
  // Desktop: normal positioning, Mobile: fixed at bottom
  // For Android mobile web, position at top of keyboard using keyboardInsetHeight
  // For other platforms, use original behavior (bottom: 0px)
  const inputBottom = isDesktop 
    ? 'auto' 
    : (isKeyboardOpen 
        ? (isAndroidMobileWeb && keyboardInsetHeight > 0 
            ? `${keyboardInsetHeight}px`  // Android: position at top of keyboard
            : '0px')  // Other platforms: original behavior
        : (effectiveHasBottomNav ? '80px' : '0px'));
  
  // Input is ~60px tall, so messages end 5px above it
  // For Android mobile web, account for keyboard height + input height
  const messagesBottom = isDesktop 
    ? 'auto' 
    : (isKeyboardOpen 
        ? (isAndroidMobileWeb && keyboardInsetHeight > 0 
            ? `${keyboardInsetHeight + 65}px`  // Android mobile web: keyboard + input height
            : '65px')  // Other platforms: original behavior
        : (effectiveHasBottomNav ? '145px' : '65px'));

  // Scroll to bottom whenever messagesBottom changes - but only after transition
  useEffect(() => {
    if (messagesContainerRef.current) {
      // Wait for transition to complete before scrolling (250ms transition duration)
      const timeout = setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, KEYBOARD_SCROLL_DELAY_MS);
      
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [messagesBottom]);

  // Wrap in container on desktop for flex layout, fragment on mobile for fixed positioning
  const Container = isDesktop ? 'div' : React.Fragment;
  const containerProps = isDesktop ? { className: 'flex flex-col h-full' } : {};

  return (
    <Container {...containerProps}>
      {/* Messages container - scrollable area above input */}
      <div 
        ref={messagesContainerRef}
        className={`w-full max-w-4xl mx-auto overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 custom-scrollbar ${
          isDesktop ? 'pt-36 pb-4 flex-1' : 'pt-24 sm:pt-28'
        }`}
        style={isDesktop ? {
          // Desktop: normal flow with flex layout
          position: 'relative',
          height: 'auto',
          maxHeight: 'calc(100vh - 200px)', // Leave room for nav and input
          paddingTop: '144px', // More space between nav and chat text (pt-36 = 9rem = 144px)
          paddingBottom: '100px', // Leave room for fixed input (25px + ~75px input height)
        } : {
          // Mobile: fixed positioning
          position: 'fixed',
          top: '80px',
          bottom: messagesBottom,
          left: '50%',
          transform: 'translateX(-50%) translateZ(0)',
          width: 'calc(100% - 32px)',
          maxWidth: '1024px',
          zIndex: MESSAGES_CONTAINER_Z_INDEX,
          transition: 'bottom 0.25s cubic-bezier(0.4, 0.0, 0.2, 1)',
          willChange: 'bottom'
        }}
          >
        {messages.map((message, index) => {
          // Only make options clickable on the last coach message when not loading
          const isLastCoachMessage = message.role === 'coach' && !isLoading &&
            index === messages.map((m, i) => m.role === 'coach' ? i : -1).filter(i => i >= 0).pop();
          const isMultiSelect = isLastCoachMessage && isMultiSelectMessage(message.content);
          // For past coach messages (not the active one), strip out the ABC option lines
          // so chat history only shows the question text, not the repeated options
          const hasOptions = message.role === 'coach' && extractOptions(message.content).length > 0;
          const shouldStripOptions = hasOptions && !isLastCoachMessage;
          const displayContent = isMultiSelect
            ? stripSelectionTags(message.content)
            : shouldStripOptions
              ? stripOptionLines(message.content)
              : message.content;

          // For single-select: tap sends immediately. For multi-select: tap toggles selection.
          const optionHandler = isLastCoachMessage && !isLimitReached
            ? (isMultiSelect
              ? (text: string) => {
                  setMultiSelectChoices(prev => {
                    const next = new Set(prev);
                    if (next.has(text)) next.delete(text);
                    else next.add(text);
                    return next;
                  });
                }
              : onSendMessage)
            : undefined;

          return (
          <div
            key={index}
            className={`${message.role === 'user' ? 'flex justify-end' : ''}`}
          >
            <div
              className={`p-2.5 sm:p-3 rounded-lg break-words ${message.role === 'user'
                ? 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-white text-base sm:text-lg rounded-[75px] max-w-[70%] ml-auto'
                : 'text-white text-base sm:text-lg max-w-[95%] sm:max-w-[95%]'
                }`}
            >
              {formatMessageContent(displayContent, optionHandler, isMultiSelect ? multiSelectChoices : undefined)}
            </div>
            {/* Send button for multi-select — below options, right-aligned */}
            {isMultiSelect && multiSelectChoices.size > 0 && (
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    const selected = Array.from(multiSelectChoices).join(', ');
                    onSendMessage(selected);
                    setMultiSelectChoices(new Set());
                  }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-white font-semibold hover:opacity-90 transition-opacity active:scale-[0.98] whitespace-nowrap"
                >
                  Send ({multiSelectChoices.size})
                </button>
              </div>
            )}
          </div>
        )})}

        {isLoading && !autoContinueMode && (
          <div className="flex justify-start">
            <div className="max-w-[85%] sm:max-w-[80%] pt-2.5 pb-2.5 pr-2.5 sm:pt-3 sm:pb-3 sm:pr-3">
              <Typing />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} className="pb-2" />
      </div>

      {/* Input section - fixed at bottom on mobile, fixed 25px from bottom on desktop */}
      <div 
        className="bg-transparent rounded-none w-full"
        style={isDesktop ? {
          // Desktop: fixed at bottom with 25px spacing
          position: 'fixed',
          bottom: '25px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '1024px',
          padding: '0 12px',
          zIndex: MESSAGES_CONTAINER_Z_INDEX
        } : {
          // Mobile: fixed at bottom
          position: 'fixed',
          bottom: inputBottom,
          left: '50%',
          transform: 'translateX(-50%) translateZ(0)',
          width: 'calc(100% - 32px)',
          maxWidth: '1024px',
          zIndex: INPUT_CONTAINER_Z_INDEX,
          transition: 'bottom 0.25s cubic-bezier(0.4, 0.0, 0.2, 1)',
          willChange: 'bottom',
          padding: '5px 12px'
        }}
      >
        {showContinueButton ? (
          autoContinueMode ? (
            <div className="flex justify-center">
              <button
                disabled={true}
                className="px-8 py-3 rounded-3xl font-semibold bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-white cursor-not-allowed flex items-center space-x-3"
              >
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Preparing your session...</span>
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <button
                onClick={onContinue}
                disabled={isContinueDisabled}
                className={`px-8 py-3 rounded-3xl font-semibold transition-colors ${
                  isContinueDisabled 
                    ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                    : 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-white hover:opacity-90'
                }`}
              >
                {isContinueDisabled ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </span>
                ) : 'Continue'}
              </button>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center w-full">
            <div className="relative w-full">
              {/* Gradient border wrapper */}
              <div className="bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] rounded-[45px] p-[1px]">
                <div 
                  className="relative w-full flex items-center bg-[#333333] rounded-[45px]"
                  style={{
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none'
                  }}
                >
                  <textarea
                    ref={textareaRef}
                    onFocus={handleFocus}
                    value={isLimitReached ? '' : inputValue}
                    onChange={(e) => {
                      if (isLimitReached) {
                        console.log('[ChatInterface] Blocked onChange - isLimitReached:', isLimitReached);
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      setInputValue(e.target.value);
                      adjustTextareaHeight();
                    }}
                    onInput={(e) => {
                      if (isLimitReached) {
                        console.log('[ChatInterface] Blocked onInput - isLimitReached:', isLimitReached);
                        e.preventDefault();
                        e.stopPropagation();
                        if (textareaRef.current) {
                          textareaRef.current.value = '';
                        }
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder={isLimitReached ? limitMessage : placeholder}
                    className={`w-full pr-14 sm:pr-16 pl-4 sm:pl-5 py-3 sm:py-4 rounded-[45px] focus:outline-none resize-none text-white text-base sm:text-lg bg-transparent placeholder:text-gray-200 transition-all duration-200 ${inputValue.trim() ? 'opacity-100' : 'opacity-60'} scrollbar-hide ${isLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                    rows={1}
                    disabled={isLoading || isLimitReached}
                    readOnly={isLimitReached}
                    style={{ 
                      minHeight: `${TEXTAREA_MIN_HEIGHT}px`, 
                      maxHeight: `${TEXTAREA_MAX_HEIGHT}px`, 
                      overflowY: 'auto',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={isLoading || isLimitReached}
                    className={`absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-gray-900 transition-colors disabled:opacity-50 ${
                      isLimitReached 
                        ? 'bg-gray-500 cursor-not-allowed' 
                        : 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] hover:opacity-90'
                    }`}
                  >
                    <ArrowUp size={18} className="sm:w-6 sm:h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};
